/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Component for Advanced Filters Search Bar
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { Button, Select, Space, Input, TreeSelect, Flex } from 'antd';
import { Search as SearchIcon } from '@icon-park/react';

import { IFieldsData, QUERY_BUILDER_FILTER_TYPE } from 'lib/SearchQueryBuilder/config/entity.config';
import { hasNonEmptyValue, translateToAscii } from '@veritable/common/utils';
import { MultiSelect, RangePicker, DatePicker } from 'lib/antd';
import { MONTH_DATE_YEAR_SLASH_FORMAT, YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { customDatePresets } from 'utils/dateParser';

const getClearIcon = (onClear: VoidFunction): { clearIcon: React.ReactNode } => {
	return {
		clearIcon: <Button variant='text' type='link' danger className='h-full w-full' icon='clear' onClick={onClear} />,
	};
};

interface IProps {
	filterSchema: IFieldsData[];
	onSearch: (searchObject: { key: string; value: string } | null) => void;
	appliedSearch: AnyType;
	isSearch: boolean;
	'data-testid'?: string;
}

const SingleFieldSearchControl: React.FC<IProps> = ({
	filterSchema = [],
	onSearch,
	appliedSearch,
	isSearch,
	'data-testid': dataTestId = 'er-search',
}) => {
	const [selectedKey, setSelectedKey] = useState(appliedSearch?.key);
	const [searchValue, setSearchValue] = useState(appliedSearch?.value || null);

	const { searchFields, defaultSelectedField } = useMemo(() => {
		const searchFieldsMeta = _.sortBy(_.filter(filterSchema, { isSearchable: true }), (field) => _.toLower(field.display_name || ''));
		const defaultSelectFieldMeta = _.find(filterSchema, (item) => !!item?.isDefaultSelectedField);
		return {
			searchFields: searchFieldsMeta,
			defaultSelectedField: defaultSelectFieldMeta?.field || searchFieldsMeta?.[0]?.field,
		};
	}, [filterSchema]);

	useEffect(() => {
		if (appliedSearch?.key) {
			setSelectedKey(appliedSearch.key);
			setSearchValue(appliedSearch.value);
		} else {
			setSelectedKey(defaultSelectedField);
			setSearchValue(null);
		}
	}, [appliedSearch, searchFields, defaultSelectedField]);

	const selectOptions = useMemo(() => {
		return _.map(searchFields, (fieldDef) => {
			return {
				label: fieldDef?.display_name || fieldDef?.field,
				value: fieldDef?.field,
			};
		});
	}, [searchFields]);

	const activeFieldDefinition = useMemo(() => {
		if (!selectedKey) {
			return undefined;
		}
		return _.find(filterSchema, { field: selectedKey });
	}, [filterSchema, selectedKey]);

	const onReset = useCallback(() => {
		setSearchValue(null);
		if (isSearch) {
			onSearch({ key: selectedKey, value: null });
		}
	}, [isSearch, onSearch, selectedKey]);

	const onDropdownChange = (value: string) => {
		setSelectedKey(value);
		setSearchValue(null);
		onSearch({ key: value, value: null });
	};

	const handleSearch = () => {
		const hasValue = hasNonEmptyValue(searchValue);
		if (selectedKey && hasValue && !_.isEqual(searchValue, appliedSearch?.value)) {
			onSearch({ key: selectedKey, value: searchValue });
		} else if (hasValue && _.isEqual(searchValue, appliedSearch?.value)) {
			return;
		} else {
			onReset();
		}
	};

	const handleSelectionChange = (value: AnyType) => {
		onSearch({ key: selectedKey, value: hasNonEmptyValue(value) ? value : null });
	};

	const renderClearIcon = useMemo(() => getClearIcon(onReset), [onReset]);

	const renderInputComponent = useCallback(() => {
		if (!activeFieldDefinition) {
			return null;
		}

		const commonInputProps = {
			...activeFieldDefinition.custom_props,
			autoFocus: activeFieldDefinition.isAutoFocusedField,
			placeholder: activeFieldDefinition.custom_props?.placeholder || activeFieldDefinition.display_name,
			className: 'w-full',
			onBlur: handleSearch,
		};

		const inputSpecificProps = {
			...commonInputProps,
			onPressEnter: handleSearch, // Only for Input components
		};

		switch (activeFieldDefinition.ui_field_type) {
			case QUERY_BUILDER_FILTER_TYPE.text:
			case QUERY_BUILDER_FILTER_TYPE.number:
				return (
					<Input
						{...inputSpecificProps}
						value={searchValue}
						allowClear={renderClearIcon}
						prefix={activeFieldDefinition.custom_props?.prefix}
						onInput={(e: React.ChangeEvent<HTMLInputElement>) => translateToAscii(e)}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
					/>
				);
			case QUERY_BUILDER_FILTER_TYPE.rangePicker:
				return (
					<RangePicker
						format={YEAR_MONTH_DATE_FORMAT}
						{...commonInputProps}
						value={searchValue}
						onBlur={undefined}
						allowClear={renderClearIcon}
						presets={customDatePresets}
						onChange={(dateStrings) => {
							setSearchValue(dateStrings?.[0] || dateStrings?.[1] ? dateStrings : null);
							handleSelectionChange(dateStrings?.[0] || dateStrings?.[1] ? dateStrings : null);
						}}
					/>
				);
			case QUERY_BUILDER_FILTER_TYPE.datePicker:
				return (
					<DatePicker
						placeholder={MONTH_DATE_YEAR_SLASH_FORMAT}
						{...commonInputProps}
						value={searchValue}
						onBlur={undefined}
						allowClear={renderClearIcon}
						inputDateFormat={YEAR_MONTH_DATE_FORMAT}
						onChange={(date) => {
							setSearchValue(date);
							handleSelectionChange(date);
						}}
					/>
				);
			case QUERY_BUILDER_FILTER_TYPE.select:
				return (
					<Select
						{...commonInputProps}
						options={activeFieldDefinition.options}
						value={searchValue}
						allowClear={renderClearIcon}
						onClear={onReset}
						onChange={(value: string) => {
							setSearchValue(value);
							handleSelectionChange(value);
						}}
					/>
				);
			case QUERY_BUILDER_FILTER_TYPE.multiselect:
				return (
					<MultiSelect
						{...commonInputProps}
						{...activeFieldDefinition.custom_props}
						options={activeFieldDefinition.options}
						value={searchValue}
						allowClear={renderClearIcon}
						onClear={onReset}
						onChange={(value: string[]) => setSearchValue(value)}
					/>
				);
			case QUERY_BUILDER_FILTER_TYPE.treeSelect: {
				return (
					<TreeSelect {...commonInputProps} {...activeFieldDefinition.custom_props} treeData={activeFieldDefinition.treeData} />
				);
			}
			case QUERY_BUILDER_FILTER_TYPE.treeMultiSelect: {
				return (
					<TreeSelect
						multiple
						treeCheckable
						maxTagCount={1}
						{...commonInputProps}
						{...activeFieldDefinition.custom_props}
						value={searchValue}
						allowClear={renderClearIcon}
						onClear={onReset}
						onChange={(value: string[]) => setSearchValue(value)}
						treeData={_.map(activeFieldDefinition.treeData, (option) => ({
							label: option.label,
							value: option.value,
							children: option?.children
								? _.map(option.children, (child) => ({
										label: child.label,
										value: child.value,
									}))
								: undefined,
						}))}
					/>
				);
			}
			case QUERY_BUILDER_FILTER_TYPE.minMaxRange: {
				return (
					<Flex>
						<Flex align='center' gap={1} justify='center'>
							<Input
								{...inputSpecificProps}
								type='number'
								min={0}
								className='flex-1'
								placeholder='Min'
								prefix={null}
								value={searchValue?.[0]}
								onChange={(e) => setSearchValue([e.target.value || null, searchValue?.[1] || null])}
							/>
							<Input
								{...inputSpecificProps}
								type='number'
								min={0}
								className='flex-1'
								value={searchValue?.[1]}
								placeholder='Max'
								prefix={null}
								onChange={(e) => setSearchValue([searchValue?.[0] || null, e.target.value || null])}
							/>
						</Flex>
						{(!!searchValue?.[0] || !!searchValue?.[1]) && (
							<div className='border-search-border-color flex items-center border border-l-0 px-3'>
								{renderClearIcon?.clearIcon}
							</div>
						)}
					</Flex>
				);
			}
			default:
				return (
					<Input
						{...inputSpecificProps}
						value={searchValue}
						allowClear={renderClearIcon}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
					/>
				);
		}
	}, [activeFieldDefinition, searchValue, onReset, renderClearIcon]);

	if (searchFields.length === 0) {
		return null;
	}

	return (
		<Space.Compact data-testid={dataTestId}>
			<Select
				value={selectedKey}
				options={selectOptions}
				onChange={onDropdownChange}
				className='w-45'
				showSearch
				optionFilterProp='label'
			/>
			<div className='single-field-search-control-item z-2 w-87.5'>
				{activeFieldDefinition ? renderInputComponent() : <Input className='w-full' />}
			</div>

			<Button
				type='primary'
				className='border-l-0'
				disabled={!selectedKey || !hasNonEmptyValue(searchValue) || _.isEqual(searchValue, appliedSearch?.value)}
				onClick={handleSearch}
				icon={<SearchIcon />}
			/>
		</Space.Compact>
	);
};

export { getClearIcon };
export default SingleFieldSearchControl;
